import { useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import LoginPage from '@/pages/login/index.page';
import type { LoginStep } from '@/pages/login/login-flow';
import { splatForStep, stepFromSplat } from '@/pages/login/utils/steps';
import { parseLoginSearch } from '@/pages/login/utils/login-search';

export const Route = createFileRoute('/login/$')({
  validateSearch: parseLoginSearch,
  component: LoginRoute,
});

function LoginRoute() {
  const { _splat } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();

  // The URL is the source of truth for the step (mirrors the Ember sub-routes),
  // so refresh, browser back/forward, and direct links all land on the right
  // step. Steps that need prior state (password / 2FA) simply start empty on a
  // direct visit, as they did in Ember.
  const step = stepFromSplat(_splat);

  const onNavigateStep = useCallback(
    (next: LoginStep) => {
      // Preserve search (notably `?addAccount`) across step changes.
      void navigate({ to: '/login/$', params: { _splat: splatForStep(next) }, search: (prev) => prev });
    },
    [navigate],
  );

  const onLoggedOutShown = useCallback(() => {
    // Strip `?loggedOut` (keeping the current step + other params) so a refresh
    // doesn't re-fire the toast; replace so it doesn't add a history entry.
    void navigate({
      to: '/login/$',
      params: { _splat },
      search: ({ loggedOut: _loggedOut, ...rest }) => rest,
      replace: true,
    });
  }, [navigate, _splat]);

  return (
    <LoginPage
      step={step}
      onNavigateStep={onNavigateStep}
      initialEmail={search.email ?? ''}
      isAddingAccount={search.addAccount === true}
      origin={search.origin}
      invite={{ source: search.source, inviteType: search.invite_type }}
      emailLinkToken={search.token}
      showLoggedOut={search.loggedOut === true}
      onLoggedOutShown={onLoggedOutShown}
    />
  );
}
